import React, { useState } from "react";
import {
    Box,
    Stack,
    Button,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import moment from "moment-timezone";
import DrawingDialog from "../../DrawingDialog";

// Helper function to convert decimal to fraction
function decimalToFraction(decimal) {
    if (decimal === null || decimal === undefined || decimal === 0) {
        return "0";
    }

    // Handle negative numbers
    const isNegative = decimal < 0;
    decimal = Math.abs(decimal);

    // If it's already a whole number, return as is
    if (decimal % 1 === 0) {
        return isNegative ? `-${decimal}` : `${decimal}`;
    }

    // Common fractions for typical measurements
    const commonFractions = [
        { decimal: 0.125, fraction: "1/8" },
        { decimal: 0.1875, fraction: "3/16" },
        { decimal: 0.25, fraction: "1/4" },
        { decimal: 0.375, fraction: "3/8" },
        { decimal: 0.5, fraction: "1/2" },
        { decimal: 0.625, fraction: "5/8" },
        { decimal: 0.75, fraction: "3/4" },
        { decimal: 0.875, fraction: "7/8" },
    ];

    // Check for exact matches with common fractions
    const exactMatch = commonFractions.find(
        (f) => Math.abs(f.decimal - decimal) < 0.001
    );
    if (exactMatch) {
        return isNegative ? `-${exactMatch.fraction}` : exactMatch.fraction;
    }

    // Check for mixed numbers (whole number + fraction)
    const wholeNumber = Math.floor(decimal);
    const fractionalPart = decimal - wholeNumber;

    if (wholeNumber > 0) {
        const fractionMatch = commonFractions.find(
            (f) => Math.abs(f.decimal - fractionalPart) < 0.001
        );
        if (fractionMatch) {
            const result = `${wholeNumber} ${fractionMatch.fraction}`;
            return isNegative ? `-${result}` : result;
        }
    }

    // If no common fraction found, use a more general approach
    // Convert to fraction with reasonable denominator
    const tolerance = 0.001;
    for (let denominator = 2; denominator <= 64; denominator++) {
        const numerator = Math.round(decimal * denominator);
        if (Math.abs(decimal - numerator / denominator) < tolerance) {
            // Simplify the fraction
            const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
            const commonDivisor = gcd(numerator, denominator);
            const simplifiedNumerator = numerator / commonDivisor;
            const simplifiedDenominator = denominator / commonDivisor;

            if (simplifiedDenominator === 1) {
                return isNegative
                    ? `-${simplifiedNumerator}`
                    : `${simplifiedNumerator}`;
            }

            const result = `${simplifiedNumerator}/${simplifiedDenominator}`;
            return isNegative ? `-${result}` : result;
        }
    }

    // If all else fails, return the decimal rounded to 3 places
    return isNegative ? `-${decimal.toFixed(3)}` : decimal.toFixed(3);
}

// Simple Work Order Dialog for temporary use
function WaveguideWorkOrderDialog({
    open,
    onClose,
    order,
    user,
    onMarkChecked,
    onMarkUnchecked,
}) {
    const [openDrawingDialog, setOpenDrawingDialog] = useState(false);
    const [selectedDrawing, setSelectedDrawing] = useState(null);

    if (!order) return null;

    const dueDate = order.due_date
        ? moment(order.due_date).format("MM/DD/YY")
        : "—";
    const projectLabel = order.project
        ? `${order.project.number} ${order.project.name}`
        : "—";
    const assigneeName = order.assignee
        ? `${order.assignee.first_name || ""} ${order.assignee.last_name ||
              ""}`.trim()
        : "—";
    const drawings = Array.isArray(order.drawings) ? order.drawings : [];
    const honeycomb = [
        order.honeycomb_type && order.honeycomb_type.cell
            ? `${decimalToFraction(order.honeycomb_type.cell)}" cell`
            : null,
        order.honeycomb_type && order.honeycomb_type.thickness
            ? `${decimalToFraction(order.honeycomb_type.thickness)}" thick`
            : null,
    ]
        .filter(Boolean)
        .join(" x ");

    // Check if user has MANAGEMENT group access
    const isManagement =
        user &&
        user.groups &&
        user.groups.some((group) => group.name === "MANAGEMENT");

    // Checked information
    const checkedByName = order.checked_by
        ? `${order.checked_by.first_name || ""} ${order.checked_by.last_name ||
              ""}`.trim()
        : null;
    const checkedDate = order.checked_at
        ? moment(order.checked_at).format("MM/DD/YY")
        : null;
    const checkedDateDisplay = order.checked
        ? moment(order.checked).format("MM/DD/YY")
        : "—";
    const checkedByDisplay = order.checked_by
        ? order.checked_by.full_name ||
          `${order.checked_by.first_name || ""} ${order.checked_by.last_name ||
              ""}`.trim() ||
          "—"
        : "—";
    const handleOpenDrawing = (d) => {
        const url =
            d.document ||
            d.file_url ||
            d.file ||
            d.url ||
            d.link ||
            d.href ||
            "";
        if (!url) return;
        setSelectedDrawing({ ...d, document: url });
        setOpenDrawingDialog(true);
    };

    return (
        <Dialog fullScreen open={open} disableEscapeKeyDown>
            <DialogTitle>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={{ xs: 2, md: 0 }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    sx={{ position: "relative", pr: { xs: 5, md: 0 } }}
                >
                    <Stack
                        direction="column"
                        spacing={0.5}
                        sx={{ minWidth: 0, flex: 1 }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                minWidth: 0,
                                wordBreak: "break-word",
                                overflow: "hidden",
                                fontWeight: 700,
                            }}
                        >
                            {projectLabel}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 0, wordBreak: "break-word" }}
                        >
                            Work Order: {order.work_order_number || "—"}
                        </Typography>
                        <Box
                            sx={{
                                display: { xs: "flex", md: "none" },
                                gap: 1,
                                mt: 1,
                            }}
                        >
                            {isManagement &&
                                (order.completed ? (
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        startIcon={<CancelIcon />}
                                        onClick={() => {
                                            if (onMarkUnchecked) {
                                                onMarkUnchecked(order.id);
                                            }
                                        }}
                                        sx={{
                                            fontSize: {
                                                xs: "0.75rem",
                                                md: "0.875rem",
                                            },
                                            px: { xs: 1, md: 2 },
                                            py: { xs: 0.5, md: 1 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "none",
                                                    sm: "inline",
                                                },
                                            }}
                                        >
                                            Mark as Unchecked
                                        </Box>
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "inline",
                                                    sm: "none",
                                                },
                                            }}
                                        >
                                            Uncheck
                                        </Box>
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<VerifiedIcon />}
                                        onClick={() => {
                                            if (onMarkChecked) {
                                                onMarkChecked(order.id);
                                            }
                                        }}
                                        sx={{
                                            fontSize: {
                                                xs: "0.75rem",
                                                md: "0.875rem",
                                            },
                                            px: { xs: 1, md: 2 },
                                            py: { xs: 0.5, md: 1 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "none",
                                                    sm: "inline",
                                                },
                                            }}
                                        >
                                            Mark as Checked
                                        </Box>
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "inline",
                                                    sm: "none",
                                                },
                                            }}
                                        >
                                            Check
                                        </Box>
                                    </Button>
                                ))}
                        </Box>
                    </Stack>
                    <Box
                        sx={{
                            display: { xs: "none", md: "flex" },
                            gap: 1,
                            alignItems: "center",
                        }}
                    >
                        {isManagement &&
                            (order.completed ? (
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<CancelIcon />}
                                    onClick={() => {
                                        if (onMarkUnchecked) {
                                            onMarkUnchecked(order.id);
                                        }
                                    }}
                                    sx={{
                                        fontSize: {
                                            xs: "0.75rem",
                                            md: "0.875rem",
                                        },
                                        px: { xs: 1, md: 2 },
                                        py: { xs: 0.5, md: 1 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "none",
                                                sm: "inline",
                                            },
                                        }}
                                    >
                                        Mark as Unchecked
                                    </Box>
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "inline",
                                                sm: "none",
                                            },
                                        }}
                                    >
                                        Uncheck
                                    </Box>
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<VerifiedIcon />}
                                    onClick={() => {
                                        if (onMarkChecked) {
                                            onMarkChecked(order.id);
                                        }
                                    }}
                                    sx={{
                                        fontSize: {
                                            xs: "0.75rem",
                                            md: "0.875rem",
                                        },
                                        px: { xs: 1, md: 2 },
                                        py: { xs: 0.5, md: 1 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "none",
                                                sm: "inline",
                                            },
                                        }}
                                    >
                                        Mark as Checked
                                    </Box>
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "inline",
                                                sm: "none",
                                            },
                                        }}
                                    >
                                        Check
                                    </Box>
                                </Button>
                            ))}
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: { xs: "absolute", md: "static" },
                            top: { xs: -8, md: 0 },
                            right: { xs: -8, md: 0 },
                            color: "inherit",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ m: 2 }}>
                <Stack spacing={2}>
                    <Stack
                        direction="row"
                        spacing={3}
                        divider={<Divider orientation="vertical" flexItem />}
                    >
                        <Typography>
                            <b>Due:</b> {dueDate}
                        </Typography>
                        <Typography>
                            <b>Assigned To:</b> {assigneeName || "—"}
                        </Typography>
                    </Stack>
                    {order.checked ? (
                        <>
                            <Divider
                                sx={{
                                    borderColor: "success.main",
                                    borderWidth: 1,
                                }}
                            />
                            <Stack
                                direction="row"
                                spacing={3}
                                divider={
                                    <Divider orientation="vertical" flexItem />
                                }
                            >
                                <Typography>
                                    <b>Checked:</b> {checkedDateDisplay}{" "}
                                </Typography>
                                <Typography>
                                    <b>Checked By:</b> {checkedByDisplay}
                                </Typography>
                            </Stack>
                        </>
                    ) : null}

                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Typography sx={{ textDecoration: "underline" }}>
                        Frame & Honeycomb
                    </Typography>
                    <Stack spacing={1}>
                        <Typography>
                            <b>Frame:</b> {order.frame_type || "—"}
                        </Typography>
                        <Typography>
                            <b>Honeycomb:</b> {honeycomb || "—"}
                        </Typography>
                        <Typography>
                            <b>Man Bars:</b> {order.man_bars ? "Yes" : "No"}
                        </Typography>
                    </Stack>

                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Typography sx={{ textDecoration: "underline" }}>
                        Items
                    </Typography>
                    <Stack spacing={1}>
                        {(order.list || []).map((li) => {
                            const w =
                                li.size && li.size.width ? li.size.width : null;
                            const h =
                                li.size && li.size.height
                                    ? li.size.height
                                    : null;
                            const d =
                                li.size && li.size.diameter
                                    ? li.size.diameter
                                    : null;
                            const sizeLabel = d
                                ? `${decimalToFraction(d)}" Ø`
                                : w && h
                                ? `${decimalToFraction(
                                      w
                                  )}" × ${decimalToFraction(h)}"`
                                : "—";
                            return (
                                <Typography key={li.id}>
                                    <b>Qty {li.qty || 0}</b> — {sizeLabel}
                                </Typography>
                            );
                        })}
                        {order.notes ? (
                            <Divider
                                sx={{
                                    borderColor: "success.main",
                                    borderWidth: 1,
                                }}
                            />
                        ) : null}
                        {order.notes ? (
                            <Stack spacing={0.5}>
                                <Typography sx={{ color: "text.secondary" }}>
                                    Notes:
                                </Typography>
                                <Typography
                                    fontWeight="bold"
                                    whiteSpace="pre-wrap"
                                >
                                    {order.notes}
                                </Typography>
                            </Stack>
                        ) : null}
                        {(order.list || []).length > 0 && (
                            <>
                                <Divider
                                    sx={{
                                        my: 1,
                                        borderColor: "success.main",
                                        borderWidth: 1,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        color: "primary.main",
                                    }}
                                >
                                    Total Area:{" "}
                                    {(order.list || [])
                                        .reduce(
                                            (acc, li) =>
                                                acc +
                                                (Number(li.total_area_sq_in) ||
                                                    0),
                                            0
                                        )
                                        .toLocaleString()}{" "}
                                    sq in
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        color: "primary.main",
                                    }}
                                >
                                    Total Perimeter:{" "}
                                    {(order.list || [])
                                        .reduce(
                                            (acc, li) =>
                                                acc +
                                                (Number(li.perimeter_in) || 0),
                                            0
                                        )
                                        .toLocaleString()}{" "}
                                    in
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        color: "warning.main",
                                        mt: 1,
                                    }}
                                >
                                    Estimated Solder Time (2 minutes per in):{" "}
                                    {(
                                        (order.list || []).reduce(
                                            (acc, li) =>
                                                acc +
                                                (Number(li.perimeter_in) || 0) *
                                                    2,
                                            0
                                        ) / 60
                                    ).toFixed(1)}{" "}
                                    hours
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        color: "info.main",
                                        mt: 1,
                                    }}
                                >
                                    Estimated Frame Assembly Time (1 hour per
                                    frame):{" "}
                                    {(order.list || [])
                                        .reduce(
                                            (acc, li) =>
                                                acc + (Number(li.qty) || 0),
                                            0
                                        )
                                        .toFixed(1)}{" "}
                                    hours
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        color: "error.main",
                                        mt: 1,
                                        pt: 1,
                                        borderTop: 1,
                                        borderColor: "divider",
                                    }}
                                >
                                    Total Estimated Time:{" "}
                                    {(
                                        (order.list || []).reduce(
                                            (acc, li) =>
                                                acc +
                                                (Number(li.perimeter_in) || 0) *
                                                    2,
                                            0
                                        ) /
                                            60 +
                                        (order.list || []).reduce(
                                            (acc, li) =>
                                                acc + (Number(li.qty) || 0),
                                            0
                                        )
                                    ).toFixed(1)}{" "}
                                    hours
                                </Typography>
                            </>
                        )}
                    </Stack>
                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Typography sx={{ textDecoration: "underline" }}>
                        Drawings
                    </Typography>
                    <Stack spacing={1}>
                        <div>
                            <Divider sx={{ mt: 2 }} />
                            {drawings.length > 0 ? (
                                <Box textAlign="center">
                                    <List sx={{ mt: 0, pt: 0 }}>
                                        {drawings.map((d) => (
                                            <div key={d.id}>
                                                <ListItem disablePadding>
                                                    <ListItemButton
                                                        onClick={() =>
                                                            handleOpenDrawing(d)
                                                        }
                                                    >
                                                        <ListItemIcon>
                                                            <ArchitectureIcon color="secondary" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={d.title}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                                <Divider />
                                            </div>
                                        ))}
                                    </List>
                                </Box>
                            ) : (
                                <div></div>
                            )}
                        </div>
                    </Stack>
                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Stack spacing={1}>
                        <Typography>
                            <b>Created Date:</b>{" "}
                            {order.created
                                ? moment
                                      .utc(order.created)
                                      .local()
                                      .format("MM/DD/YY")
                                : "—"}
                        </Typography>
                        <Typography>
                            <b>Created By:</b>{" "}
                            {order.created_by
                                ? `${order.created_by.first_name || ""} ${order
                                      .created_by.last_name || ""}`.trim()
                                : "—"}
                        </Typography>
                        <Typography>
                            <b>Last Updated:</b>{" "}
                            {order.updated
                                ? moment
                                      .utc(order.updated)
                                      .local()
                                      .format("MM/DD/YY h:mm A")
                                : "—"}
                        </Typography>
                        {order.is_complete && (
                            <Typography>
                                <b>Completed At:</b>{" "}
                                {order.completed
                                    ? moment
                                          .utc(order.completed)
                                          .local()
                                          .format("MM/DD/YY h:mm A")
                                    : "—"}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>

            {selectedDrawing && (
                <DrawingDialog
                    openDrawingDialog={openDrawingDialog}
                    setOpenDrawingDialog={setOpenDrawingDialog}
                    drawing={selectedDrawing}
                />
            )}
        </Dialog>
    );
}

export default WaveguideWorkOrderDialog;
